/**
 * Spec 054, step 3b: the client's accesses — the safe link's page and «Доступы» on the client's
 * screen, in words, kept apart from the screens so a test reads them. The link's code stands after
 * «#» of the page's address: a browser never sends that part to a server.
 */
import type { AccessUseOut, ClientAccessOut } from "@/api/orders";
import { clock } from "@/lib/lead";

export const MAX_ENTRIES = 10;
export const MAX_VALUE = 64 * 1024;

export type ResourceKind = "server" | "domain" | "repository" | "bot" | "account" | "subscription" | "service" | "other";
export type ValueKind = "password" | "key" | "token" | "other";

export const KINDS: { value: ResourceKind; label: string }[] = [
  { value: "server", label: "Сервер" },
  { value: "domain", label: "Домен" },
  { value: "repository", label: "Репозиторий" },
  { value: "bot", label: "Бот" },
  { value: "account", label: "Аккаунт" },
  { value: "subscription", label: "Подписка" },
  { value: "service", label: "Сервис" },
  { value: "other", label: "Другое" },
];

export const VALUE_KINDS: { value: ValueKind; label: string }[] = [
  { value: "password", label: "Пароль" },
  { value: "key", label: "Ключ (SSH и другие)" },
  { value: "token", label: "Токен" },
  { value: "other", label: "Другое" },
];

const KIND_LABEL: Record<string, string> = Object.fromEntries(KINDS.map((k) => [k.value, k.label]));

export interface AccessEntry {
  title: string;
  kind: ResourceKind;
  address: string;
  login: string;
  value: string;
  value_kind: ValueKind;
  comment: string;
}

export const emptyEntry = (): AccessEntry => ({
  title: "", kind: "server", address: "", login: "", value: "", value_kind: "password", comment: "",
});

/** The code from the page's address «#…», or null — none, or not one the tasker gives. */
export function codeFrom(hash: string): string | null {
  const code = hash.replace(/^#/, "").trim();
  return /^[A-Za-z0-9_-]{16,128}$/.test(code) ? code : null;
}

/** A name without a value, or a value without a name: the page will not send it half. */
export function halfFilled(e: AccessEntry): boolean {
  return Boolean(e.title.trim()) !== Boolean(e.value);
}

/** What goes to the server: the filled entries, the text fields trimmed, the value as typed —
 * a key's last line break is part of it. */
export function entriesToSend(entries: AccessEntry[]) {
  return entries
    .filter((e) => e.title.trim() && e.value)
    .map((e) => ({
      title: e.title.trim(),
      kind: e.kind,
      address: e.address.trim() || null,
      login: e.login.trim() || null,
      value: e.value,
      value_kind: e.value_kind,
      comment: e.comment.trim() || null,
    }));
}

/** What the page says when the server would not take the accesses. */
export function refusalText(status: number, detail: unknown): string {
  if (status === 410) return "Ссылка уже использована или устарела. Попросите у менеджера новую.";
  if (status === 503) return "Сейчас не можем принять доступы. Попробуйте через несколько минут.";
  const message = detail && typeof detail === "object" ? (detail as { message?: unknown }).message : null;
  if (status === 422 && typeof message === "string") return message;
  if (status === 422) return "Проверьте поля: что-то заполнено не так.";
  if (status === 0) return "Нет связи с сервером. Проверьте интернет и нажмите «Сохранить» ещё раз.";
  return "Не получилось сохранить. Попробуйте ещё раз.";
}

/** «Сервер · 10.0.0.5 · логин admin · из З-1». */
export function accessLine(a: Pick<ClientAccessOut, "kind" | "address" | "login" | "order_label">): string {
  const parts = [KIND_LABEL[a.kind] ?? a.kind];
  if (a.address) parts.push(a.address);
  if (a.login) parts.push(`логин ${a.login}`);
  if (a.order_label) parts.push(`из ${a.order_label}`);
  return parts.join(" · ");
}

const USE_WORDS: Record<string, string> = {
  link_issued: "получил ссылку на файл",
  downloaded: "взял файлом",
  revealed: "открыл в приложении",
};

/** Who took a value: «Разработчик · З-1 · запуск 1a2b3c4d · взял файлом · Костя · 01:23». */
export function useLine(u: AccessUseOut, now: Date = new Date()): string {
  return [u.note, USE_WORDS[u.kind] ?? u.kind, u.who, clock(u.at, now)].filter(Boolean).join(" · ");
}
